import { canUsePreferredHomeApp } from "./home-routing.js";

export const POST_SIGN_IN_ACCESS_PENDING_PATH = "/access-pending";
export const POST_SIGN_IN_DEFAULT_HOME = "hub";

/**
 * @param {string} nextPath
 * @returns {boolean}
 */
export function isAccessPendingNextPath(nextPath) {
  const p = String(nextPath || "")
    .trim()
    .replace(/\/+$/, "");
  return p === POST_SIGN_IN_ACCESS_PENDING_PATH || p === "/zugriff-ausstehend";
}

/**
 * Reine Post-Sign-In-Navigationsentscheidung (Password-Login + Auth-Callback).
 *
 * IST-Divergenz Superadmin/Pending bewusst erhalten via `applyPendingWaitGate`:
 * - Password-Login: `applyPendingWaitGate: true` (+ `isSuperadmin` / `pendingWait`)
 * - Auth-Callback: `applyPendingWaitGate: false` (kein Pending-Gate)
 *
 * Keine DOM-Side-Effects — Aufrufer führt `assign` / `navigateTo` aus.
 *
 * @param {object} [sessionCtx]
 * @param {boolean} [sessionCtx.hasSession=true]
 * @param {boolean} [sessionCtx.forcePasswordReset=false]
 * @param {string} [sessionCtx.inviteCompletionPath=""]
 * @param {boolean} [sessionCtx.accessPendingNext=false]
 * @param {string} [sessionCtx.nextPath=""]
 * @param {'assign'|'navigateTo'} [sessionCtx.nextPathMethod='navigateTo']
 * @param {string} [sessionCtx.storedInviteUrl=""]
 * @param {boolean} [sessionCtx.applyPendingWaitGate=false]
 * @param {boolean} [sessionCtx.isSuperadmin=false]
 * @param {{ ok?: boolean, shouldWait?: boolean } | null} [sessionCtx.pendingWait]
 * @param {string} [sessionCtx.syncPreferred="hub"]
 * @param {string} [sessionCtx.asyncHomeRoute="hub"]
 * @returns {{ method: 'assign'|'navigateTo', target: string, reason: string }}
 */
export function resolvePostSignInNavigation(sessionCtx = {}) {
  const {
    hasSession = true,
    forcePasswordReset = false,
    inviteCompletionPath = "",
    accessPendingNext = false,
    nextPath = "",
    nextPathMethod = "navigateTo",
    storedInviteUrl = "",
    applyPendingWaitGate = false,
    isSuperadmin = false,
    pendingWait = null,
    syncPreferred = POST_SIGN_IN_DEFAULT_HOME,
    asyncHomeRoute = POST_SIGN_IN_DEFAULT_HOME,
  } = sessionCtx;

  if (!hasSession) {
    return {
      method: "navigateTo",
      target: "/login",
      reason: "no-session",
    };
  }

  if (forcePasswordReset) {
    return {
      method: "navigateTo",
      target: "/reset-password",
      reason: "password-reset",
    };
  }

  const invitePath = String(inviteCompletionPath || "").trim();
  if (invitePath) {
    return {
      method: "navigateTo",
      target: invitePath,
      reason: "invite-completion",
    };
  }

  if (accessPendingNext || isAccessPendingNextPath(nextPath)) {
    return {
      method: "navigateTo",
      target: POST_SIGN_IN_ACCESS_PENDING_PATH,
      reason: "access-pending-next",
    };
  }

  const next = String(nextPath || "").trim();
  if (next) {
    return {
      method: nextPathMethod === "assign" ? "assign" : "navigateTo",
      target: next,
      reason: "next-path",
    };
  }

  const storedInvite = String(storedInviteUrl || "").trim();
  if (storedInvite) {
    return {
      method: "assign",
      target: storedInvite,
      reason: "stored-invite",
    };
  }

  // Password-Login IST only: Superadmin skips pending wait; Callback passes false.
  if (
    applyPendingWaitGate &&
    !isSuperadmin &&
    pendingWait?.ok &&
    pendingWait?.shouldWait
  ) {
    return {
      method: "navigateTo",
      target: POST_SIGN_IN_DEFAULT_HOME,
      reason: "pending-wait",
    };
  }

  if (canUsePreferredHomeApp(syncPreferred)) {
    return {
      method: "navigateTo",
      target: String(syncPreferred).trim(),
      reason: "preferred-home",
    };
  }

  return {
    method: "navigateTo",
    target: String(asyncHomeRoute || "").trim() || POST_SIGN_IN_DEFAULT_HOME,
    reason: "async-home",
  };
}
